import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import Confetti from 'growth-onboarding-shared-demo-components/transitions/Confetti';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useSetErrorAfterTimeout } from '../hooks/useSetErrorAfterTimeout';
import { getActiveLoading, getCanSwitchCopy } from '../store/loading-status/selectors';
import { LoadingCentered, LoadingIllustration, LoadingImage } from './Loading.styles';
import { LoadingTitle } from './LoadingTitle';
import { useCopyCarousel } from './useCopyCarousel';
import { useLoadingTitle } from './useLoadingTitle';
import styled from 'styled-components';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { LayoutType } from 'signup-components/types';
import { devices } from 'signup-constants/styles/viewports.styles';
import UILoadingDots from 'UIComponents/loading/UILoadingDots';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { useExperiment, TrackWhen } from '../experiments/useExperiment';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledLoadingContainer = styled.div.withConfig({
  displayName: "BaseLoadingView__StyledLoadingContainer",
  componentId: "sc-5t4w95-0"
})(["", " .AirplaneGif{padding-left:10px;}@media ", "{.AirplaneGif{padding-left:30px;}}"], props => (props.layoutType === LayoutType.FramedCentredStep || props.layoutType === LayoutType.FramedCentredModalStep) && `  margin: auto;
    vertical-align: middle;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);`, devices.tablet);
const StyledTextContainer = styled.div.withConfig({
  displayName: "BaseLoadingView__StyledTextContainer",
  componentId: "sc-5t4w95-1"
})(["min-height:30px;"]);
const BaseLoadingView = ({
  loadingKey,
  hasSpinner = true,
  imageUrl,
  imageClass,
  textDisplayDelay = 0,
  isIllustration,
  hasConfetti = false,
  illustrationHeight,
  hasLoadingDots,
  setTimeoutForCopyCarousel = 0
}) => {
  const {
    titleI18n,
    subtitleI18n
  } = useLoadingTitle({
    loadingKey
  });
  const canSwitchCopy = useSelector(getCanSwitchCopy(loadingKey));
  const {
    copyToDisplay: titleToDisplay
  } = useCopyCarousel({
    baseI18n: titleI18n,
    loadingKey,
    canSwitchCopy,
    textDisplayDelay,
    setTimeoutForCopyCarousel
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSpinner, setShouldShowSpinner] = useState(true);
  const [shouldShowText, setShouldShowText] = useState(textDisplayDelay === 0);
  const shouldNotAnimate = Boolean(useSelector(getActiveLoading));
  useSetErrorAfterTimeout(loadingKey);
  const {
    isAssignedToVariant: isACQ0149Variant
  } = useExperiment(ExperimentKeys.ACQ0149, TrackWhen.never);
  const {
    layoutType
  } = useLayoutTemplate();
  const renderLoadingIcon = () => {
    const shouldRenderSpinner = hasSpinner || !imageLoaded && showSpinner;
    if (hasLoadingDots || isACQ0149Variant()) {
      return /*#__PURE__*/_jsx(UILoadingDots, {
        grow: true,
        minHeight: 100
      });
    }
    return shouldRenderSpinner && /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      minHeight: 100,
      size: "md"
    });
  };
  const renderImage = () => {
    if (isIllustration && imageUrl) {
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(LoadingIllustration, {
          alt: "Loading illustration",
          className: imageClass,
          height: illustrationHeight,
          name: imageUrl,
          onLoad: () => {
            setImageLoaded(true);
            setShouldShowSpinner(false);
          }
        })
      });
    }
    return imageUrl && /*#__PURE__*/_jsx("div", {
      style: {
        display: imageLoaded ? 'block' : 'none'
      },
      children: /*#__PURE__*/_jsx(LoadingImage, {
        className: imageClass,
        src: imageUrl,
        onLoad: () => {
          setImageLoaded(true);
          setShouldShowSpinner(false);
        }
      })
    });
  };
  const renderText = () => {
    if ((imageUrl && imageLoaded || showSpinner) && shouldShowText) {
      return /*#__PURE__*/_jsxs(StyledTextContainer, {
        children: [isValidI18nKey(titleToDisplay) && /*#__PURE__*/_jsx(LoadingTitle, {
          loadingKey: loadingKey,
          title: titleToDisplay
        }), isValidI18nKey(subtitleI18n) && /*#__PURE__*/_jsx(FormattedMessage, {
          message: subtitleI18n
        })]
      });
    }
    return null;
  };
  useEffect(() => {
    const timeout = setTimeout(() => setShouldShowText(true), textDisplayDelay);
    return () => {
      clearTimeout(timeout);
    };
  }, [shouldShowText, textDisplayDelay]);
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    disableAnimation: shouldNotAnimate,
    children: [hasConfetti && /*#__PURE__*/_jsx(Confetti, {}), /*#__PURE__*/_jsx(NavMarker, {
      name: "LOADING"
    }), /*#__PURE__*/_jsx(StyledLoadingContainer, {
      "data-selenium": loadingKey,
      layoutType: layoutType,
      children: /*#__PURE__*/_jsxs(LoadingCentered, {
        children: [renderLoadingIcon(), renderImage(), renderText()]
      })
    })]
  });
};
export default BaseLoadingView;